/* Auto-advancing step card (sections/reformly-bp-steps.liquid).

   One step is open at a time; the card advances on a timer and the open step's
   rule fills over that same duration. Clicking a step opens it and restarts the
   clock, so a reader who takes over is not fought by the timer.

   Two things it deliberately does NOT do:

     - run off screen. The timer only ticks while the card is visible, so a
       reader who scrolls back finds the step they left rather than whichever
       one the clock landed on.
     - auto-advance under prefers-reduced-motion. The steps stay clickable;
       they just do not move on their own.

   On phones each step carries its OWN copy of its shot, so nothing moves when
   the open step changes. The panel used to be relocated between rows, but a
   DOM move is instant: the closing step's image disappeared the moment it
   started collapsing, instead of staying put while the row animated shut.
   The copies cost no extra requests — same URLs, so the second reference is a
   cache hit. */
(function () {
  'use strict';

  /* 640, not 899. The card goes one column at 899, but between 641 and 899 the
     media panel simply stacks below the accordion as its own block (order: 2 on
     the app card). It only travels into the open step at phone width. */
  var PHONE = '(max-width: 640px)';

  /* Fallback only. The real value is --rbp-steps-dissolve in
     reformly-bp-sections.css, which differs per variant (900 on the routine
     card, 500 on the app card), so it is read off the element rather than
     duplicated here — retiring the outgoing shot before its fade has finished
     would cut the dissolve short. */
  var DISSOLVE = 900;

  function dissolveMs(root) {
    var v = getComputedStyle(root).getPropertyValue('--rbp-steps-dissolve').trim();
    var n = parseFloat(v);
    if (!n) return DISSOLVE;
    return /ms$/.test(v) ? n : n * 1000;
  }

  function init(root) {
    var items = root.querySelectorAll('[data-rbp-step]');
    var shots = root.querySelectorAll('[data-rbp-shot]');
    if (!items.length) return;

    var wait = parseInt(root.dataset.interval, 10) || 9000;
    /* Only the routine card runs a timer. The app card is click-only, so it
       gets the same open/close behaviour with nothing advancing it. */
    var auto = root.hasAttribute('data-autoplay');
    var still = window.matchMedia('(prefers-reduced-motion: reduce)');
    var phone = window.matchMedia(PHONE);
    var index = 0;
    var prev = -1;
    var timer = null;
    var retire = null;
    var visible = false;
    var dissolve = dissolveMs(root);

    /* The fill is a transitioned height, matching the source's rtGo():
       reset to 0 over 260ms, then 40ms later kick it to 100% over the step's
       full duration. The 40ms gap matters — setting height twice in the same
       frame collapses into one style change and the transition never runs. */
    function runFill(item, live) {
      /* Nothing to drive on a card with no timer. The divided (app) card hides
         the rail outright — `.rbp-steps--divided .rbp-steps__rail { display:
         none }` — and never kicks the fill, so the two writes below only cost
         it six inline style mutations and six 260ms transitions per click,
         landing in the same frames as the row animation. */
      if (!auto) return;

      var fill = item.querySelector('.rbp-steps__fill');
      if (!fill) return;

      fill.style.transition = 'height 260ms ease';
      fill.style.height = '0%';

      if (!live || !auto || still.matches) return;

      clearTimeout(fill._kick);
      fill._kick = setTimeout(function () {
        fill.style.transition = 'height ' + wait + 'ms linear';
        fill.style.height = '100%';
      }, 40);
    }

    /* Desktop, app card only: every row opens to the SAME height, so closing
       one and opening another cancel and the card never resizes. Measured from
       the tallest row because the copy is merchant-editable. */
    function sizePanels() {
      if (!root.classList.contains('rbp-steps--divided')) return;
      if (window.matchMedia('(max-width: 899px)').matches) {
        root.style.removeProperty('--rbp-steps-panel-h');
        return;
      }
      var tall = 0;
      for (var i = 0; i < items.length; i++) {
        var inner = items[i].querySelector('.rbp-steps__reveal')
          || items[i].querySelector('.rbp-steps__inner');
        if (inner && inner.scrollHeight > tall) tall = inner.scrollHeight;
      }

      if (tall) {
        root.style.setProperty('--rbp-steps-panel-h', tall + 'px');
      } else if (!sizePanels.retried) {
        sizePanels.retried = true;
        requestAnimationFrame(sizePanels);
      }
    }

    /* Phones, app card only: each row gets an explicit pixel height so the
       closing row animates shut over the same 500ms the opening row takes.
       Rows differ in height here because only the open one shows its media. */
    function sizeMobilePanels() {
      if (!root.classList.contains('rbp-steps--divided')) return;
      if (!phone.matches) {
        /* ONCE, not on every step change. This runs from go() on each tap, and
           at desktop its only job is to undo the phone's inline heights — but
           writing to six panels invalidates layout in the very frame the
           transition is supposed to start in, which showed up as a single long
           frame at the moment of the switch. Desktop rows are sized by
           --rbp-steps-panel-h in CSS, so once cleared they stay cleared until
           the breakpoint changes and this runs again. */
        if (sizeMobilePanels.cleared) return;
        sizeMobilePanels.cleared = true;
        for (var c = 0; c < items.length; c++) {
          var clear = items[c].querySelector('.rbp-steps__panel');
          if (clear) clear.style.removeProperty('height');
        }
        return;
      }
      sizeMobilePanels.cleared = false;
      /* READ every height, THEN write every height — never interleaved.

         This used to measure one row and immediately set its height before
         measuring the next. Each write invalidates layout, so the next
         scrollHeight read forces a synchronous reflow: six of them, in the
         click handler, in the frame the animation is supposed to start on.
         That is layout thrashing, and it is why the phone hitched right at the
         beginning of the open.

         Splitting the loops costs one reflow instead of six. */
      var panels = [];
      var tallest = 0;
      for (var i = 0; i < items.length; i++) {
        var panel = items[i].querySelector('.rbp-steps__panel');
        if (!panel) { panels.push(null); continue; }
        var body = panel.firstElementChild;
        var h = body ? body.scrollHeight : 0;
        if (h > tallest) tallest = h;
        panels.push(panel);
      }

      /* ONE height for every row, exactly as the desktop card does with
         --rbp-steps-panel-h — and for the same reason.

         Each row used to open to its own content height. The rows all carry the
         same 1:1 image, so they differ only by how many lines of copy they
         have, but that is enough: closing one row and opening another then does
         NOT cancel, the list nets the difference, and everything below it
         shifts by that amount inside the same 500ms. The bigger the mismatch
         the faster the whole card appears to move — which is exactly the
         reported symptom, that stepping forward one row feels right while
         jumping from 1 to 4, or back from 5 to 3, goes too quick.

         Measured from the tallest row HERE, on the phone, with the image
         included. An earlier attempt reused the desktop variable, which is
         measured from copy alone with no media in the row — far too short, so
         it cropped the image and hid the text. */
      for (var w = 0; w < panels.length; w++) {
        if (panels[w]) panels[w].style.height = w === index ? tallest + 'px' : '0px';
      }
    }

    /* Warm the clip that is about to be needed.

       preload="auto" is a HINT, and iOS ignores it until the user interacts —
       so on a phone the next step's clip would start downloading only once it
       was already being faded in. Calling load() on the upcoming clip while the
       current one plays gives it the ~9s of the step to buffer. */
    /* Pull every shot image down as soon as the card is on screen.

       The markup can only mark the FIRST one eager — a lazy image that sits at
       opacity 0 inside an overflow:hidden panel gives the browser no reason to
       fetch it, so the first step change faded in an image that had not
       arrived. It popped instead of ramping.

       Flipping `loading` to eager is what starts the load: the spec says an
       image whose lazy state is turned off resumes loading immediately.
       decode() then does the expensive part — turning the bytes into a bitmap —
       off the frame that needs to paint it, which is the difference between a
       smooth fade and a hitch on the first tap.

       Cheap enough to do all at once: these are ~670px webp renditions, and
       the alternative (fetching one step ahead like warmNext does for video)
       still leaves the FIRST change unprotected, which is the one being
       complained about. */
    function warmShots() {
      /* Warm the set that is actually ON SCREEN, one image at a time.

         Two things here, both learned the hard way.

         WHICH SET. The desktop card cross-fades [data-rbp-shot] in its own
         column; a phone hides that column outright and shows
         .rbp-steps__stepmedia, a copy inside each row. Warming both meant
         twelve images on a phone, six of them in a display:none subtree that
         will never be painted.

         ONE AT A TIME. decode() is not free — it turns bytes into a bitmap on
         (or alongside) the main thread, and firing all of them together put a
         burst of that work exactly where the card becomes interactive. That is
         invisible on a fast phone and is precisely the kind of thing that reads
         as "not smooth" on a mid-range one. Chaining means the work is spread
         out and a tap can interleave with it.

         Still comfortably ahead of need: the chain starts when the card enters
         the viewport, long before anyone has read far enough to tap a step. */
      var sel = phone.matches
        ? '.rbp-steps__stepmedia img'
        : '[data-rbp-shot] img';
      var imgs = root.querySelectorAll(sel);

      var i = 0;
      (function next() {
        while (i < imgs.length && imgs[i]._rtWarm) i++;
        if (i >= imgs.length) return;

        var img = imgs[i++];
        img._rtWarm = 1;
        if (img.loading === 'lazy') img.loading = 'eager';

        if (!img.decode) { next(); return; }
        img.decode().catch(function () {}).then(function () {
          /* Yield between images so a tap is never queued behind the rest of
             the chain. */
          if (window.requestIdleCallback) window.requestIdleCallback(next, { timeout: 500 });
          else setTimeout(next, 0);
        });
      })();
    }

    function warmNext() {
      /* Nothing is fetched until the card is actually on screen. Warming from
         page load put ~3MB on the wire while the visitor was still in the hero,
         competing with whatever they chose to play up there. */
      if (!visible) return;

      var n = (index + 1) % items.length;
      for (var s = 0; s < shots.length; s++) {
        if (parseInt(shots[s].dataset.rbpShot, 10) !== n) continue;
        var vid = shots[s].querySelector('video');
        if (!vid) continue;
        if (vid.preload !== 'auto') vid.preload = 'auto';
        if (vid.readyState < 2) vid.load();

        /* Park it on frame 0 as soon as it has enough data. Doing the seek HERE
           — during the 9s the previous step is running — is what lets the swap
           itself be instant. */
        if (vid.readyState >= 1) {
          if (vid.currentTime > 0.05) { try { vid.currentTime = 0; } catch (e) {} }
        } else if (!vid._rtSeek) {
          vid._rtSeek = function () {
            vid.removeEventListener('loadedmetadata', vid._rtSeek);
            vid._rtSeek = null;
            try { vid.currentTime = 0; } catch (e) {}
          };
          vid.addEventListener('loadedmetadata', vid._rtSeek);
        }
      }
    }

    function paint() {
      for (var i = 0; i < items.length; i++) {
        var on = i === index;
        items[i].classList.toggle('is-active', on);
        var head = items[i].querySelector('.rbp-steps__head');
        if (head) head.setAttribute('aria-expanded', on ? 'true' : 'false');
        runFill(items[i], on);
      }

      for (var s = 0; s < shots.length; s++) {
        var n = parseInt(shots[s].dataset.rbpShot, 10);
        var live = n === index;
        var outgoing = n === prev && prev !== index;

        /* NEVER PROMOTE A SHOT THAT IS STILL FADING IN.

           `is-prev` sets opacity 1 to hold the outgoing image up under the
           incoming one, which is right when the outgoing one is already
           opaque. Interrupt a dissolve, though — tap 3 while 2 is still
           arriving — and the shot being retired is mid-fade, so `is-prev`
           animates it UP to 1 while the incoming one is still near 0. The
           result is the intermediate step showing at full strength for a
           moment: the reported "1 to 3 renders 2 for a split second", which is
           why it only happens on a quick second tap and never from rest.

           Below the threshold it gets no class at all, so the base rule fades
           it out from wherever it had reached, on the same curve. Nothing
           snaps, and the panel still never dips to grey because the shot that
           WAS opaque is the one holding underneath. */
        if (outgoing && parseFloat(getComputedStyle(shots[s]).opacity) < 0.98) {
          outgoing = false;
        }

        shots[s].classList.toggle('is-active', live);
        /* The clip being replaced holds at full opacity UNDER the incoming one
           for the length of the dissolve, so the panel never dips toward its
           background. */
        shots[s].classList.toggle('is-prev', outgoing);

        var vid = shots[s].querySelector('video');
        if (!vid) continue;

        /* Only the live clip loops; the outgoing one plays on through the
           dissolve and is paused at the end of it. Anything else stops now. */
        vid.loop = live;
        if (live || outgoing) {
          var p = vid.play();
          if (p && p.catch) p.catch(function () {});
        } else {
          vid.pause();
        }
      }

      /* Retire the outgoing shot once the fade has finished. */
      clearTimeout(retire);

      function retireNow() {
        for (var r = 0; r < shots.length; r++) {
          if (shots[r].classList.contains('is-active')) continue;
          shots[r].classList.remove('is-prev');
          var v = shots[r].querySelector('video');
          if (v) v.pause();
        }
      }

      /* Hold the OUTGOING image up until the incoming one can actually be
         painted. It sits underneath at full opacity, so for as long as it stays
         the panel shows the old step rather than the panel's grey while the new
         image is still decoding — which is what a cold first tap used to look
         like. warmShots() makes this the rare path, not the normal one.

         Nothing here delays the step change itself: the classes are already
         swapped above and the fade is already running. This only moves when the
         layer underneath is taken away. */
      var live = null;
      for (var a = 0; a < shots.length; a++) {
        if (shots[a].classList.contains('is-active')) { live = shots[a].querySelector('img'); break; }
      }

      if (live && !live.complete && live.decode) {
        live.decode().catch(function () {}).then(function () {
          clearTimeout(retire);
          retire = setTimeout(retireNow, dissolve);
        });
      } else {
        retire = setTimeout(retireNow, dissolve);
      }

      sizeMobilePanels();
      warmNext();
    }

    /* The step change is never blocked by the video.

       This used to seek the incoming clip and wait for `seeked` before
       committing, so the first advance stalled on step 1 until that clip had
       downloaded — the wait WAS the stall. The seek now happens in warmNext()
       while the previous step plays, so by the time a clip is needed it is
       already parked on frame 0 and the swap is immediate.

       A cold clip simply shows its poster for a moment instead of holding the
       whole card. */
    function go(i) {
      var next = (i + items.length) % items.length;
      if (next === index) return;

      for (var s = 0; s < shots.length; s++) {
        if (parseInt(shots[s].dataset.rbpShot, 10) !== next) continue;
        var vid = shots[s].querySelector('video');
        /* Only rewind if it is sitting on an old frame; seeking a clip that is
           already at 0 costs a decode for nothing. */
        if (vid && vid.readyState >= 1 && vid.currentTime > 0.05) {
          try { vid.currentTime = 0; } catch (e) { /* not seekable yet */ }
        }
      }

      prev = index;
      index = next;
      paint();
    }

    function start() {
      stop();
      if (!auto || still.matches) return;
      timer = setInterval(function () { go(index + 1); }, wait);
    }

    function stop() {
      if (timer) clearInterval(timer);
      timer = null;
    }

    for (var i = 0; i < items.length; i++) {
      (function (n) {
        var head = items[n].querySelector('.rbp-steps__head');
        if (head) {
          head.addEventListener('click', function () {
            go(n);
            start();
          });
        }
      })(i);
    }

    /* Re-home the panel when the layout flips, not just when the step does. */
    function onBreakpoint() {
      sizeMobilePanels();
      /* The other media set is the visible one now — warm that instead. Rotating
         a phone into landscape crosses this, and the newly-shown copies would
         otherwise be cold. */
      if (visible) warmShots();
    }
    if (phone.addEventListener) phone.addEventListener('change', onBreakpoint);
    else if (phone.addListener) phone.addListener(onBreakpoint);

    sizePanels();
    paint();

    /* Re-measure when the line count can change. */
    var rid;
    window.addEventListener('resize', function () {
      clearTimeout(rid);
      rid = setTimeout(function () {
        sizePanels();
        sizeMobilePanels();
      }, 150);
    });
    /* Both, not just the desktop one: the shared phone height is measured from
       the tallest row's COPY, so it is wrong until the real face has loaded and
       the line count has settled. */
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(function () {
        sizePanels();
        sizeMobilePanels();
      });
    }

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        visible = entries[0].isIntersecting;
        if (visible) {
          /* The 9s cycle begins HERE, not at page load.

             The timer already started on visibility, but the rail's fill was
             kicked by paint() at init — so it ran its 9s off screen and was
             sitting full by the time anyone saw it, with the first advance
             arriving at some arbitrary point of the animation. Restarting the
             fill alongside the timer puts the two back in step. */
          runFill(items[index], true);
          start();
          /* First chance to fetch: the card has just come into view. */
          warmShots();
          warmNext();
        } else {
          stop();
          /* Wind the rail back so it does not resume mid-fill on return. */
          runFill(items[index], false);
        }
      }, { threshold: 0.25 }).observe(root);
    } else {
      visible = true;
      start();
    }
  }

  function boot(scope) {
    (scope || document).querySelectorAll('[data-rbp-steps]').forEach(init);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { boot(); });
  else boot();

  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
})();
